import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import HeroSelection from './components/HeroSelection';
import RequestForm from './components/RequestForm';
// Lazy load heavy dashboard components with error handling
const lazyWithRetry = (componentImport: () => Promise<any>) => {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      console.error('Lazy loading error:', error);
      // Retry once after a short delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      return await componentImport();
    }
  });
};

const ProviderDashboard = lazyWithRetry(() => import('./components/ProviderDashboard'));
const BrokerDashboard = lazyWithRetry(() => import('./components/BrokerDashboard'));
const AdminDashboard = lazyWithRetry(() => import('./components/AdminDashboard'));
const CustomerDashboard = lazyWithRetry(() => import('./components/CustomerDashboard'));
const AuthModal = lazyWithRetry(() => import('./components/AuthModal'));
const PaymentResult = lazyWithRetry(() => import('./components/PaymentResult'));
const MobileUpload = lazyWithRetry(() => import('./components/MobileUpload'));
import PaymentModal from './components/PaymentModal';
import { Category, User, UserRole, CreditPackage } from './types';
import { api } from './lib/db';
import { WifiOff, X, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import ErrorBoundary from './components/ui/ErrorBoundary';
import CookieConsent from './components/ui/CookieConsent';
import { usePageTitle } from './hooks/usePageTitle';
import StaticPages from './components/StaticPages';
import DesignDemo from './components/DesignDemo';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';

// Loading fallback component
const DashboardLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600 dark:text-indigo-400 mb-4" />
      <p className="text-gray-600 dark:text-gray-400">Yükleniyor...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// FIX: Moved ProtectedRoute outside component to prevent type errors and re-creation
const ProtectedRoute = ({ children, allowedRoles, currentUser }: { children: React.ReactNode, allowedRoles?: UserRole[], currentUser: User | null }) => {
  if (!currentUser) return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(currentUser.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
};

function AppContent() {
  // FIX: Initialize state directly from storage to prevent flash of "not logged in" state on refresh
  const [currentUser, setCurrentUser] = useState<User | null>(() => api.session.get());

  const [showAuthModal, setShowAuthModal] = useState<'LOGIN' | 'REGISTER' | null>(null);
  const [isBackendHealthy, setIsBackendHealthy] = useState(true);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [dopingPaymentDetails, setDopingPaymentDetails] = useState<{
    planName: string;
    price: number;
    onConfirm: (details: any) => Promise<void>;
  } | null>(null);

  // Initialize theme based on system preference immediately
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  const { addToast } = useToast();

  const navigate = useNavigate();
  const location = useLocation();

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/yonetici')) return 'Yönetici Paneli';
    if (path.includes('/kurumsal')) return 'Kurumsal Panel';
    if (path.includes('/musteri')) return 'Taleplerim';
    if (path.includes('/talep-olustur')) return 'Talep Oluştur';
    return 'Emlak & Oto Pazar Yeri';
  };
  usePageTitle(getPageTitle());

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  useEffect(() => {
    // Check backend health and load packages
    const init = async () => {
      try {
        // First check health endpoint (lighter, faster)
        const healthResponse = await fetch('/api/health');
        if (!healthResponse.ok) throw new Error('Health check failed');

        setIsBackendHealthy(true);

        // Then load stats and packages
        try {
          await api.public.getStats();
          const pkgs = await api.settings.getPackages();
          setPackages(pkgs);
        } catch (statsError) {
          // Stats/packages failed but backend is alive
          console.warn('Stats/packages load failed, but backend is healthy:', statsError);
        }
      } catch (error) {
        console.error('Backend health check failed:', error);
        setIsBackendHealthy(false);
      }
    };
    init();

    // Periodic health check every 30 seconds
    const healthCheckInterval = setInterval(async () => {
      try {
        const healthResponse = await fetch('/api/health');
        if (healthResponse.ok) {
          setIsBackendHealthy(true);
        } else {
          setIsBackendHealthy(false);
        }
      } catch (error) {
        setIsBackendHealthy(false);
      }
    }, 30000);

    return () => clearInterval(healthCheckInterval);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    if (theme === 'dark') html.classList.add('dark');
    else html.classList.remove('dark');
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  const handleLogout = () => {
    api.session.clear();
    setCurrentUser(null);
    navigate('/');
    addToast('Başarıyla çıkış yapıldı', 'info');
  };

  const handleLoginSuccess = (user: User) => {
    api.session.save(user);
    setCurrentUser(user);
    setShowAuthModal(null);
    addToast(`Hoş geldin, ${user.fullName || user.companyName}`, 'success');

    if (user.role === UserRole.ADMIN) navigate('/panel/yonetici');
    else if (user.role === UserRole.BROKER) navigate('/panel/broker');
    else if (user.role === UserRole.PROVIDER) navigate('/panel/kurumsal/firsatlar');
    else if (user.role === UserRole.CUSTOMER) navigate('/panel/musteri/talepler');
  };

  const handleBuyCredits = async (paymentDetails: any) => {
    if (currentUser && selectedPackage) {
      try {
        const totalWithVAT = selectedPackage.price * 1.20;

        // Bu işlem sadece PayTR token'ı döner, kullanıcı verisini güncellemez
        const response = await api.transactions.buyCredits(
          currentUser.id,
          totalWithVAT,
          selectedPackage.credits,
          `${selectedPackage.name} Satın Alımı (+ KDV)`,
          paymentDetails
        );

        // Yanıtı PaymentModal'a geri döndür
        return response;

      } catch (error: any) {
        addToast(error.message || 'Ödeme işlemi başlatılamadı.', 'error');
        throw error;
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-sans text-gray-900 dark:text-gray-100 transition-colors duration-300">
      {!isBackendHealthy && (
        <div className="bg-red-600 text-white p-2 text-sm font-bold text-center flex items-center justify-center animate-pulse">
          <WifiOff className="w-4 h-4 mr-2" /> Sunucuya Bağlanılamıyor. Lütfen internetinizi kontrol edin.
        </div>
      )}

      {/* Header is global, but hidden on Admin dashboard based on route check if desired, kept simplified here */}
      {location.pathname !== '/panel/yonetici' && location.pathname !== '/proje-teknik' && (
        <Header
          currentUser={currentUser}
          onLogout={handleLogout}
          onOpenAuth={setShowAuthModal}
          theme={theme}
          toggleTheme={toggleTheme}
        />
      )}

      <main className="transition-all duration-300">
        <ErrorBoundary>
          <Suspense fallback={<DashboardLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HeroSelection theme={theme} />} />
              <Route path="/talep-olustur/:categoryStr" element={<RequestForm />} />

              {/* Payment Result Routes */}
              <Route path="/payment/:status" element={
                <Suspense fallback={<DashboardLoader />}>
                  <PaymentResult />
                </Suspense>
              } />

              {/* Mobile Upload Route */}
              <Route path="/mobile-upload/:sessionId" element={<MobileUpload />} />

              {/* Static Pages */}
              <Route path="/hakkimizda" element={<StaticPages view="ABOUT" onBack={() => navigate(-1)} />} />
              <Route path="/nasil-calisir" element={<StaticPages view="HOW_IT_WORKS" onBack={() => navigate(-1)} />} />
              <Route path="/iletisim" element={<StaticPages view="CONTACT" onBack={() => navigate(-1)} />} />
              <Route path="/gizlilik" element={<StaticPages view="PRIVACY" onBack={() => navigate(-1)} />} />
              <Route path="/sozlesme" element={<StaticPages view="TERMS" onBack={() => navigate(-1)} />} />
              <Route path="/iade-kosullari" element={<StaticPages view="RETURN_POLICY" onBack={() => navigate(-1)} />} />
              <Route path="/mesafeli-satis-sozlesmesi" element={<StaticPages view="DISTANCE_SALES" onBack={() => navigate(-1)} />} />

              {/* Design Demo - Yeni tasarım sistemi önizlemesi */}
              <Route path="/design-demo" element={<DesignDemo onClose={() => navigate(-1)} />} />

              {/* Protected Routes - Provider */}
              <Route path="/panel/kurumsal/:tab?" element={
                <ProtectedRoute allowedRoles={[UserRole.PROVIDER]} currentUser={currentUser}>
                  <ProviderDashboard
                    currentUser={currentUser!}
                    onBuyCredits={() => setShowPricingModal(true)}
                    onUpdateUser={(u: User) => { setCurrentUser(u); api.session.save(u); }}
                  />
                </ProtectedRoute>
              } />

              {/* Protected Routes - Broker */}
              <Route path="/panel/broker/:tab?" element={
                <ProtectedRoute allowedRoles={[UserRole.BROKER]} currentUser={currentUser}>
                  <BrokerDashboard
                    user={currentUser!}
                    addToast={addToast}
                    onUpdateUser={(u: User) => { setCurrentUser(u); api.session.save(u); }}
                  />
                </ProtectedRoute>
              } />

              {/* Protected Routes - Customer */}
              <Route path="/panel/musteri/:tab?" element={
                <ProtectedRoute allowedRoles={[UserRole.CUSTOMER]} currentUser={currentUser}>
                  <CustomerDashboard
                    currentUser={currentUser!}
                    onUpdateUser={(u: User) => { setCurrentUser(u); api.session.save(u); }}
                    onPurchaseDoping={(details: { planName: string; price: number; onConfirm: (d: any) => Promise<void>; }) => setDopingPaymentDetails(details)}
                  />
                </ProtectedRoute>
              } />

              {/* Protected Routes - Admin */}
              <Route path="/panel/yonetici/:tab?" element={
                <ProtectedRoute allowedRoles={[UserRole.ADMIN]} currentUser={currentUser}>
                  <AdminDashboard onLogout={handleLogout} />
                </ProtectedRoute>
              } />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      {showAuthModal && (
        <Suspense fallback={null}>
          <AuthModal type={showAuthModal} onClose={() => setShowAuthModal(null)} onSuccess={handleLoginSuccess} />
        </Suspense>
      )}

      {/* Pricing Modal */}
      {showPricingModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm transition-opacity" onClick={() => setShowPricingModal(false)}></div>

          <div className="relative bg-[#0F172A] border border-gray-800 rounded-[2rem] w-full max-w-6xl max-h-[95vh] overflow-y-auto custom-scrollbar shadow-2xl animate-in zoom-in-95 duration-200">
            <button
              onClick={() => setShowPricingModal(false)}
              className="absolute top-6 right-6 p-2 bg-gray-800 text-gray-400 hover:text-white rounded-full transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-8 md:p-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Kurumsal Hizmet Paketleri</h2>
                <p className="text-gray-400 text-lg">İhtiyacınıza en uygun dijital teklif paketini seçerek hemen başlayın.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                {packages.map(p => (
                  <div
                    key={p.id}
                    className={`relative flex flex-col p-8 rounded-3xl transition-all duration-300 ${p.popular
                      ? 'bg-[#1E293B]/80 border-2 border-indigo-500 shadow-xl shadow-indigo-500/10 scale-100 md:scale-105 z-10'
                      : 'bg-[#1E293B]/40 border border-gray-700 hover:border-gray-600'
                      }`}
                  >
                    {p.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-bold uppercase py-1.5 px-4 rounded-full shadow-lg tracking-wider">
                        En Çok Tercih Edilen
                      </div>
                    )}

                    <div className="text-center mb-8">
                      <h3 className="text-xl font-bold text-white mb-4">{p.name}</h3>
                      <div className="flex items-end justify-center gap-1">
                        <span className="text-5xl font-black text-white tracking-tight">{p.price} ₺</span>
                      </div>
                      <span className="text-gray-400 text-xs font-medium block mt-2">(+ %20 KDV)</span>
                    </div>

                    <div className="flex-1 space-y-6 mb-8">
                      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                        <div className="flex items-center gap-2 mb-1">
                          <Info className="w-3 h-3 text-gray-400" />
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">PAKET İÇERİĞİ</span>
                        </div>
                        <div className="flex items-start gap-3 mt-3">
                          <CheckCircle2 className={`w-5 h-5 mt-0.5 shrink-0 ${p.popular ? 'text-green-400' : 'text-green-500/70'}`} />
                          <div>
                            <span className="block font-bold text-white text-sm">{p.credits} Adet Teklif Verme Hakkı</span>
                            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                              Bu hizmet paketi, sistemdeki müşteri taleplerine <strong className="text-gray-300">{p.credits} adet</strong> dijital teklif verme hakkı sağlar.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => { setSelectedPackage(p); setShowPricingModal(false); }}
                      className={`w-full py-4 rounded-xl font-bold text-base transition-all shadow-lg active:scale-95 ${p.popular
                        ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-500/25'
                        : 'bg-white hover:bg-gray-100 text-gray-900'
                        }`}
                    >
                      Paketi Seç
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-12 text-center border-t border-gray-800 pt-8">
                <p className="text-xs text-gray-500 max-w-3xl mx-auto leading-relaxed">
                  Satın alınan hizmet kapsamındaki haklar iade edilemez ve devredilemez. Tüm ödeme işlemleri PayTR altyapısı ve 256-bit SSL güvencesiyle gerçekleştirilmektedir.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedPackage && (
        <PaymentModal
          theme={theme}
          planName={selectedPackage.name}
          price={Number((selectedPackage.price * 1.20).toFixed(2))}
          credits={selectedPackage.credits}
          onClose={() => setSelectedPackage(null)}
          onConfirm={handleBuyCredits}
        />
      )}
      {dopingPaymentDetails && (
        <PaymentModal
          theme={theme}
          planName={dopingPaymentDetails.planName}
          price={dopingPaymentDetails.price}
          credits={0}
          onClose={() => setDopingPaymentDetails(null)}
          onConfirm={dopingPaymentDetails.onConfirm}
        />
      )}
      <CookieConsent />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}